import { useEffect, useMemo, useRef, useState } from "react";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { useSeason } from "@/lib/SeasonContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Upload, Plus, ChevronLeft, ChevronRight, Download, BarChart3, Pencil, Trash2, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { downloadIcs } from "@/lib/ics";
import { Link } from "react-router-dom";

function startOfMonth(d) { return new Date(d.getFullYear(), d.getMonth(), 1); }
function fmtDate(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`; }

export default function Schedule() {
  const { user } = useAuth();
  const { activeSeason } = useSeason();
  const [allGames, setAllGames] = useState([]);
  const [cursor, setCursor] = useState(new Date());
  const [dialogOpen, setDialogOpen] = useState(false);
  const [dialogEditing, setDialogEditing] = useState(null); // full game object when editing, null when creating
  const [dialogPrefillDate, setDialogPrefillDate] = useState(""); // date pre-filled when opened from calendar click
  const [reassigning, setReassigning] = useState(false);
  const fileRef = useRef(null);

  const openAddDialog = (prefillDate = "") => {
    setDialogEditing(null);
    setDialogPrefillDate(prefillDate);
    setDialogOpen(true);
  };
  const openEditDialog = (game) => {
    setDialogEditing(game);
    setDialogPrefillDate("");
    setDialogOpen(true);
  };
  const closeDialog = () => {
    setDialogOpen(false);
    setDialogEditing(null);
    setDialogPrefillDate("");
  };

  const deleteGame = async (game) => {
    if (!confirm(`Delete game vs ${game.opponent} on ${game.date}?\n\nThis also removes all stats saved for this game. Can't be undone.`)) return;
    try {
      await api.delete(`/games/${game.id}`);
      toast.success("Game deleted");
      load();
    } catch (err) { toast.error(formatError(err)); }
  };

  const reassignSeasons = async () => {
    if (!confirm("Recompute every game's season from its date?\n\nBasketball season runs Aug–Jul. A game played 2027-01-15 belongs to 2026-27. This fixes games that were tagged to the wrong season.")) return;
    setReassigning(true);
    try {
      const { data } = await api.post("/seasons/reassign-by-date");
      const parts = [
        `${data.changed} game${data.changed === 1 ? "" : "s"} re-tagged`,
        data.unchanged > 0 && `${data.unchanged} already correct`,
        data.skipped > 0 && `${data.skipped} skipped (bad date)`,
      ].filter(Boolean).join(" · ");
      toast.success(`Reassign complete — ${parts}`, { duration: 8000 });
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setReassigning(false); }
  };

  const load = () => api.get("/games").then((r) => setAllGames(r.data));
  useEffect(() => { load(); }, [activeSeason]);

  // Games from the currently-viewed season — drive the "All Games" table + .ics export
  const games = useMemo(
    () => (activeSeason ? allGames.filter((g) => g.season === activeSeason) : allGames),
    [allGames, activeSeason],
  );
  // Games from every other season — rendered in yellow on the calendar so the coach can
  // see past/future season games alongside the current one without needing a toggle.
  const otherSeasonGames = useMemo(
    () => (activeSeason ? allGames.filter((g) => g.season !== activeSeason) : []),
    [allGames, activeSeason],
  );

  const byDate = useMemo(() => {
    const m = {};
    for (const g of games) {
      (m[g.date] = m[g.date] || []).push({ ...g, _crossSeason: false });
    }
    for (const g of otherSeasonGames) {
      (m[g.date] = m[g.date] || []).push({ ...g, _crossSeason: true });
    }
    return m;
  }, [games, otherSeasonGames]);

  // build calendar grid
  const monthStart = startOfMonth(cursor);
  const startDay = monthStart.getDay();
  const daysInMonth = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0).getDate();
  const grid = [];
  for (let i = 0; i < startDay; i++) grid.push(null);
  for (let i = 1; i <= daysInMonth; i++) grid.push(new Date(cursor.getFullYear(), cursor.getMonth(), i));
  while (grid.length % 7 !== 0) grid.push(null);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/games/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      if (data.added === 0 && data.skipped_duplicates === 0) {
        // Nothing valid found — surface a helpful error with what we saw vs what we expected
        const found = (data.columns_detected || []).slice(0, 6).join(", ") || "(no headers detected)";
        toast.error(
          `No games imported from ${file.name}. This file doesn't look like a game schedule. Detected columns: ${found}. Expected: DATE, OPPONENT, H/A, NPHS PTS, OPP PTS.`,
          { duration: 9000 }
        );
      } else if (data.added === 0) {
        toast.info(`No new games — all ${data.skipped_duplicates} row(s) already exist for ${data.season}.`);
      } else {
        toast.success(`Uploaded to ${data.season}: ${data.added} new · ${data.skipped_duplicates} duplicate${data.skipped_duplicates === 1 ? "" : "s"}${data.invalid_rows ? ` · ${data.invalid_rows} skipped (missing date/opponent)` : ""}`);
      }
      load();
    } catch (err) {
      toast.error(formatError(err));
    } finally { e.target.value = ""; }
  };

  return (
    <div className="p-6 md:p-10">
      <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
        <div>
          <p className="eyebrow" data-testid="schedule-season-label">{activeSeason ? `${activeSeason} Season` : "Season"}</p>
          <h1 className="heading-1">Schedule</h1>
        </div>
        {user && (
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={() => downloadIcs(games)} disabled={games.length === 0} data-testid="schedule-ics-btn">
              <Download className="w-4 h-4 mr-2" /> .ics
            </Button>
            <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={reassignSeasons} disabled={reassigning} data-testid="schedule-fix-seasons-btn" title="Recompute each game's season from its date (Aug–Jul rule). Use if games are showing under the wrong season.">
              <Wand2 className="w-4 h-4 mr-2" /> {reassigning ? "Fixing…" : "Fix Seasons"}
            </Button>
            <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={() => fileRef.current?.click()} data-testid="schedule-upload-btn">
              <Upload className="w-4 h-4 mr-2" /> Upload
            </Button>
            <input ref={fileRef} type="file" accept=".xlsx,.csv" className="hidden" onChange={handleUpload} />
            <Button className="bg-burgundy hover:bg-burgundy-hover" onClick={() => openAddDialog()} data-testid="schedule-add-game-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Game
            </Button>
          </div>
        )}
        {!user && games.length > 0 && (
          <Button variant="outline" className="border-ink-700 hover:bg-ink-800" onClick={() => downloadIcs(games)} data-testid="schedule-ics-btn-public">
            <Download className="w-4 h-4 mr-2" /> Add to Calendar
          </Button>
        )}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 text-xs uppercase tracking-wider text-ink-300 mb-4 flex-wrap">
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-burgundy rounded-sm" /> Home</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-ink-500 rounded-sm" /> Away</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-amber-500 rounded-sm" /> Playoff</span>
        <span className="inline-flex items-center gap-2"><span className="w-3 h-3 bg-yellow-300 rounded-sm border border-yellow-500" /> Other Season</span>
      </div>

      {/* Calendar */}
      <div className="bg-ink-800 rounded-lg border border-white/5 p-4 mb-6">
        <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() - 1, 1))} className="p-2 hover:bg-ink-700 rounded" data-testid="cal-prev">
            <ChevronLeft className="w-4 h-4" />
          </button>

          <div className="flex items-center gap-2">
            <Select
              value={String(cursor.getMonth())}
              onValueChange={(v) => setCursor(new Date(cursor.getFullYear(), Number(v), 1))}
            >
              <SelectTrigger className="w-[130px] bg-ink-900 border-ink-700 text-white font-display font-bold uppercase tracking-wider h-9" data-testid="cal-month-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ink-900 border-ink-700">
                {["January","February","March","April","May","June","July","August","September","October","November","December"].map((m, i) => (
                  <SelectItem key={m} value={String(i)} className="text-white">{m}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select
              value={String(cursor.getFullYear())}
              onValueChange={(v) => setCursor(new Date(Number(v), cursor.getMonth(), 1))}
            >
              <SelectTrigger className="w-[100px] bg-ink-900 border-ink-700 text-white font-display font-bold uppercase tracking-wider h-9" data-testid="cal-year-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-ink-900 border-ink-700 max-h-64">
                {(() => {
                  const cur = new Date().getFullYear();
                  const years = new Set();
                  for (let y = 2020; y <= cur + 2; y++) years.add(y);
                  // include any years present in games list
                  for (const g of games) { if (g.date) years.add(Number(g.date.slice(0, 4))); }
                  years.add(cursor.getFullYear());
                  return Array.from(years).sort((a, b) => b - a).map((y) => (
                    <SelectItem key={y} value={String(y)} className="text-white">{y}</SelectItem>
                  ));
                })()}
              </SelectContent>
            </Select>

            <button
              onClick={() => setCursor(new Date())}
              className="text-[10px] uppercase tracking-wider text-ink-300 hover:text-white border border-ink-700 hover:bg-ink-700 rounded px-2 py-1.5 transition"
              data-testid="cal-today-btn"
              title="Jump to current month"
            >
              Today
            </button>
          </div>

          <button onClick={() => setCursor(new Date(cursor.getFullYear(), cursor.getMonth() + 1, 1))} className="p-2 hover:bg-ink-700 rounded" data-testid="cal-next">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
        <div className="grid grid-cols-7 gap-1 text-center text-xs uppercase tracking-wider text-ink-400 mb-1">
          {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((d) => <div key={d}>{d}</div>)}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {grid.map((d, i) => {
            if (!d) return <div key={i} className="h-20 md:h-24 rounded bg-ink-900/50" />;
            const key = fmtDate(d);
            const events = byDate[key] || [];
            // Coach-only: clicking the day opens the Add Game dialog pre-filled with this date
            const dayClickable = !!user;
            return (
              <div
                key={i}
                className={`h-20 md:h-24 rounded bg-ink-900 border border-ink-700/40 p-1.5 overflow-hidden transition ${
                  dayClickable ? "cursor-pointer hover:border-burgundy/60 hover:bg-ink-900/70" : ""
                }`}
                onClick={dayClickable ? (e) => {
                  // Only fire if the click hit the day cell background (not an event pill)
                  if (e.target === e.currentTarget || e.target.dataset?.dayBg === "1") {
                    openAddDialog(key);
                  }
                } : undefined}
                data-testid={`cal-day-${key}`}
                title={dayClickable ? `Click to add a game on ${key}` : undefined}
              >
                <div data-day-bg="1" className="text-xs text-ink-400 font-semibold">{d.getDate()}</div>
                <div data-day-bg="1" className="space-y-1 mt-1">
                  {events.slice(0, 2).map((g) => (
                    <div
                      key={g.id}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (user && !g._crossSeason) openEditDialog(g);
                      }}
                      className={`text-[10px] truncate px-1.5 py-0.5 rounded ${
                        g._crossSeason
                          ? "bg-yellow-300 text-black border border-yellow-500"
                          : g.playoff
                            ? "bg-amber-500 text-white"
                            : g.home_away === "home"
                              ? "bg-burgundy text-white"
                              : "bg-ink-500 text-white"
                      } ${user && !g._crossSeason ? "cursor-pointer hover:brightness-125" : ""}`}
                      title={
                        g._crossSeason
                          ? `${g.opponent} · ${g.season}`
                          : user ? `Click to edit — vs ${g.opponent}` : g.opponent
                      }
                      data-testid={g._crossSeason ? `cal-cross-season-event-${g.id}` : `cal-event-${g.id}`}
                    >
                      {g.home_away === "home" ? "vs" : "@"} {g.opponent}
                    </div>
                  ))}
                  {events.length > 2 && <div data-day-bg="1" className="text-[9px] text-ink-400">+{events.length - 2}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Game list */}
      <div className="bg-ink-800 rounded-lg border border-white/5 overflow-hidden">
        <div className="px-6 py-4 border-b border-ink-700/60">
          <h3 className="heading-3">All Games</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead className="bg-ink-900 text-ink-300">
              <tr className="text-left">
                <th className="px-4 py-3 uppercase text-xs tracking-wider">#</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Date</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Opponent</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">H/A</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Score</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Location</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Notes</th>
                <th className="px-4 py-3 uppercase text-xs tracking-wider">Box</th>
                {user && <th className="px-4 py-3 uppercase text-xs tracking-wider text-right">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {games.length === 0 && (
                <tr><td colSpan={user ? 9 : 8} className="text-center text-ink-400 py-10">No games scheduled yet.</td></tr>
              )}
              {games.map((g, idx) => {
                const result = g.nphs_score !== null && g.opp_score !== null
                  ? (g.nphs_score > g.opp_score ? "W" : "L")
                  : null;
                return (
                  <tr key={g.id} className="border-t border-ink-700/40 hover:bg-ink-900/40" data-testid={`game-row-${g.id}`}>
                    <td className="px-4 py-3 text-ink-400">{g.game_number || idx + 1}</td>
                    <td className="px-4 py-3">{g.date}</td>
                    <td className="px-4 py-3 font-semibold">{g.opponent}{g.playoff && <span className="ml-2 text-amber-400 text-[10px] uppercase">Playoff</span>}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded text-xs uppercase ${g.home_away === "home" ? "bg-burgundy text-white" : "bg-ink-700 text-ink-200"}`}>
                        {g.home_away}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      {result ? (
                        <span className="font-display font-bold">
                          <span className={result === "W" ? "text-green-400" : "text-red-400"}>{result}</span> {g.nphs_score}-{g.opp_score}
                        </span>
                      ) : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-ink-300">{g.location || "—"}</td>
                    <td className="px-4 py-3 text-ink-300">{g.notes || "—"}</td>
                    <td className="px-4 py-3">
                      <Link to={`/game/${g.id}`} className="inline-flex items-center gap-1 text-xs text-burgundy hover:text-burgundy-hover uppercase tracking-wider" data-testid={`game-boxscore-link-${g.id}`}>
                        <BarChart3 className="w-3.5 h-3.5" /> View
                      </Link>
                    </td>
                    {user && (
                      <td className="px-4 py-3 text-right whitespace-nowrap">
                        <button
                          onClick={() => openEditDialog(g)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-ink-300 hover:text-white hover:bg-ink-700 rounded px-2 py-1 mr-1 transition"
                          data-testid={`game-edit-${g.id}`}
                          title="Edit game"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          onClick={() => deleteGame(g)}
                          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wider text-red-400 hover:text-red-300 hover:bg-red-500/10 border border-transparent hover:border-red-500/40 rounded px-2 py-1 transition"
                          data-testid={`game-delete-${g.id}`}
                          title="Delete game"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <GameDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSaved={() => { closeDialog(); load(); }}
        editing={dialogEditing}
        prefillDate={dialogPrefillDate}
      />
    </div>
  );
}

function GameDialog({ open, onClose, onSaved, editing, prefillDate }) {
  const emptyForm = { date: "", opponent: "", home_away: "home", nphs_score: "", opp_score: "", location: "", notes: "", playoff: false };
  const [form, setForm] = useState(emptyForm);
  const [busy, setBusy] = useState(false);

  // Whenever the dialog opens, reset form based on edit target or prefill date
  useEffect(() => {
    if (!open) return;
    if (editing) {
      setForm({
        date: editing.date || "",
        opponent: editing.opponent || "",
        home_away: editing.home_away || "home",
        nphs_score: editing.nphs_score ?? "",
        opp_score: editing.opp_score ?? "",
        location: editing.location || "",
        notes: editing.notes || "",
        playoff: !!editing.playoff,
      });
    } else {
      setForm({ ...emptyForm, date: prefillDate || "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, editing, prefillDate]);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = {
        ...form,
        nphs_score: form.nphs_score === "" || form.nphs_score === null ? null : +form.nphs_score,
        opp_score: form.opp_score === "" || form.opp_score === null ? null : +form.opp_score,
      };
      if (editing) {
        // Preserve fields the edit form doesn't expose so a PUT can't clobber them.
        if (editing.season) payload.season = editing.season;
        if (editing.game_number != null) payload.game_number = editing.game_number;
        await api.put(`/games/${editing.id}`, payload);
        toast.success("Game updated");
      } else {
        await api.post("/games", payload);
        toast.success("Game added");
      }
      onSaved();
    } catch (err) {
      toast.error(formatError(err));
    } finally { setBusy(false); }
  };

  const isEdit = !!editing;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="bg-ink-900 border-ink-700 text-white max-w-lg">
        <DialogHeader>
          <DialogTitle className="heading-3">{isEdit ? "Edit Game" : "Add Game"}</DialogTitle>
          <DialogDescription className="text-ink-300">
            {isEdit
              ? "Update any field — including flipping between regular season and playoff. Saves in place."
              : "Add a single game manually. Use Upload for full schedules."}
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3" data-testid={isEdit ? "edit-game-form" : "add-game-form"}>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="eyebrow">Date</Label>
              <Input type="date" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-date" />
            </div>
            <div>
              <Label className="eyebrow">Opponent</Label>
              <Input value={form.opponent} onChange={(e) => setForm({ ...form, opponent: e.target.value })} required className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-opponent" />
            </div>
            <div>
              <Label className="eyebrow">H/A</Label>
              <Select value={form.home_away} onValueChange={(v) => setForm({ ...form, home_away: v })}>
                <SelectTrigger className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-ha"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-ink-900 border-ink-700">
                  <SelectItem value="home">Home</SelectItem>
                  <SelectItem value="away">Away</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="eyebrow">Location</Label>
              <Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-location" />
            </div>
            <div>
              <Label className="eyebrow">NPHS Pts</Label>
              <Input value={form.nphs_score} onChange={(e) => setForm({ ...form, nphs_score: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-nphs" />
            </div>
            <div>
              <Label className="eyebrow">Opp Pts</Label>
              <Input value={form.opp_score} onChange={(e) => setForm({ ...form, opp_score: e.target.value })} inputMode="numeric" className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-opp" />
            </div>
          </div>
          <div>
            <Label className="eyebrow">Notes</Label>
            <Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="bg-ink-800 border-ink-700 mt-2" data-testid="game-form-notes" />
          </div>
          <label className="inline-flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={form.playoff}
              onChange={(e) => setForm({ ...form, playoff: e.target.checked })}
              data-testid="game-form-playoff"
            />
            <span>Playoff game</span>
          </label>
          <Button type="submit" disabled={busy} className="w-full bg-burgundy hover:bg-burgundy-hover" data-testid="game-form-submit">
            {busy ? "Saving…" : (isEdit ? "Save Changes" : "Add Game")}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
